import React, { useState } from "react";

//material ui imports
import {
  Grid,
  TextField,
  Typography,
  Divider,
  Button,
  Stack,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SaveIcon from "@mui/icons-material/Save";

//project imports
import config from "../../../../config";

//third party imports
import * as Yup from "yup";
import { useFormik } from "formik";
import useAxiosAuth from "../../../../hooks/useAxiosAuth";
import { LoadingButton } from "@mui/lab";
import {
  convertDateToLocal,
  convertDateToLocalReverse,
} from "../../../../utils/date-operations";

const WorkExperienceForm = (props) => {
  //Constants
  const axiosAuth = useAxiosAuth();
  const { onSave, onCancel, selectedWorkExperience } = props;
  const theme = useTheme();

  //States
  const [loading, setLoading] = useState(false);
  // const [isCurrentChecked, setIsCurrentChecked] = useState(
  //   selectedWorkExperience?.isCurrent
  // );

  const formik = useFormik({
    initialValues: {
      id: selectedWorkExperience.id,
      candidateId: selectedWorkExperience.candidateId,
      companyName: selectedWorkExperience.companyName,
      position: selectedWorkExperience.position,
      salary: selectedWorkExperience.salary,
      experience: selectedWorkExperience.experience,
      fromDate: convertDateToLocalReverse(selectedWorkExperience.fromDate),
      toDate: selectedWorkExperience.tillToday
        ? null
        : convertDateToLocalReverse(selectedWorkExperience.toDate),
      tillToday: selectedWorkExperience.tillToday,
    },
    validationSchema: Yup.object({
      companyName: Yup.string()
        .max(50, "Must be 50 characters or less")
        .required("Required"),
      position: Yup.string()
        .max(50, "Must be 50 characters or less")
        .required("Required"),
      salary: Yup.number()
        .min(0, "Salary cannot be negative")
        .required("Required"),
      experience: Yup.number()
        .min(0, "Experience cannot be negative")
        .required("Required"),
      fromDate: Yup.date()
        .max(new Date(), "From date cannot be in the future")
        .required("Required"),
      toDate: Yup.date().when("tillToday", {
        is: false,
        then: Yup.date()
          .min(Yup.ref("fromDate"), "To date must be after from date")
          .max(new Date(), "To date cannot be in the future"),

        otherwise: Yup.date().nullable(),
      }),
    }),
    onSubmit: (values) => {
      if (values.tillToday) {
        values.toDate = null;
      }
      setLoading(true);
      if (selectedWorkExperience.id === 0) {
        axiosAuth
          .post("/api/CandidateWorkExpriences", values)
          .then((response) => {
            if (response.status === 200) {
              onSave();
            } else {
              setLoading(false);
            }
          })
          .catch((error) => {
            console.log(error);
            setLoading(false);
          });
      } else {
        axiosAuth
          .put("/api/CandidateWorkExpriences", values)
          .then((response) => {
            if (response.status === 200) {
              onSave();
            } else {
              setLoading(false);
            }
          })
          .catch((error) => {
            console.log(error);
            setLoading(false);
          });
      }
    },
  });

  const handleCurrentChange = (e) => {
    formik.setFieldValue("tillToday", e.target.checked);
    if (e.target.checked) {
      formik.setFieldValue("toDate", null);
    }
  };

  return (
    <>
      <Grid container>
        {selectedWorkExperience.id === 0 ? (
          <Stack direction="row" sx={{ marginX: "auto", alignItems: "center" }}>
            <AccountBalanceIcon />
            <Typography
              color={theme.palette.primary.dark}
              variant="h5"
              textAlign="center"
              sx={{ marginLeft: 2 }}
            >
              Add New Work Experience
            </Typography>
          </Stack>
        ) : (
          <Stack direction="row" sx={{ marginX: "auto", alignItems: "center" }}>
            <AccountBalanceIcon />
            <Typography
              color={theme.palette.primary.dark}
              variant="h5"
              textAlign="center"
              sx={{ marginLeft: 2 }}
            >
              Edit Experience Details
            </Typography>
          </Stack>
        )}
      </Grid>
      <Divider sx={{ marginY: 2 }} />
      <Grid container spacing={2}>
        <Grid item xl={12} lg={12} md={12} sm={12}>
          <form onSubmit={formik.handleSubmit}>
            <TextField
              name="companyName"
              label="Company Name *"
              onChange={formik.handleChange}
              value={formik.values.companyName}
              fullWidth
              error={
                formik.touched.companyName && Boolean(formik.errors.companyName)
              }
              helperText={
                formik.touched.companyName && formik.errors.companyName
              }
              sx={{ marginBottom: 2 }}
            />

            <TextField
              name="position"
              label="Position *"
              onChange={formik.handleChange}
              value={formik.values.position}
              fullWidth
              error={formik.touched.position && Boolean(formik.errors.position)}
              helperText={formik.touched.position && formik.errors.position}
              sx={{ marginBottom: 2 }}
            />

            <TextField
              name="salary"
              label="Salary / Month *"
              type="number"
              fullWidth
              value={formik.values.salary}
              onChange={(e) => {
                let value = e.target.value;
                // Remove leading zeroes unless it's just 0
                if (/^0[0-9]+/.test(value)) {
                  value = value.replace(/^0+/, "");
                }
                formik.setFieldValue("salary", value);
              }}
              error={formik.touched.salary && Boolean(formik.errors.salary)}
              helperText={formik.touched.salary && formik.errors.salary}
              sx={{ marginBottom: 2 }}
            />

            <TextField
              name="experience"
              label="Experience (Years) *"
              type="number"
              fullWidth
              value={formik.values.experience}
              onChange={(e) => {
                let value = e.target.value;
                // Remove leading zeroes
                if (/^0[0-9]+/.test(value)) {
                  value = value.replace(/^0+/, "");
                }
                formik.setFieldValue("experience", value);
              }}
              error={
                formik.touched.experience && Boolean(formik.errors.experience)
              }
              helperText={formik.touched.experience && formik.errors.experience}
              sx={{ marginBottom: 3 }}
            />
            <TextField
              name="fromDate"
              type="date"
              label="From Date"
              onChange={formik.handleChange}
              value={formik.values.fromDate}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={formik.touched.fromDate && Boolean(formik.errors.fromDate)}
              helperText={formik.touched.fromDate && formik.errors.fromDate}
              sx={{ marginBottom: 2 }}
            />

            <FormControlLabel
              sx={{ marginBottom: 2 }}
              label="Till Today"
              control={
                <Checkbox
                  checked={formik.values.tillToday}
                  onChange={(e) => {
                    formik.setFieldValue("tillToday", e.target.checked);
                    if (e.target.checked) {
                      formik.setFieldValue("toDate", null);
                    }
                  }}
                />
              }
            />

            {!formik.values.tillToday && (
              <TextField
                name="toDate"
                type="date"
                label="To Date *"
                onChange={formik.handleChange}
                value={formik.values.toDate || ""}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={formik.touched.toDate && Boolean(formik.errors.toDate)}
                helperText={formik.touched.toDate && formik.errors.toDate}
              />
            )}

            <Divider sx={{ my: 2 }} />
            <Grid>
              <LoadingButton
                variant="contained"
                type="submit"
                loading={loading}
                loadingPosition="end"
                endIcon={<SaveIcon />}
              >
                Submit
              </LoadingButton>
              <Button
                variant="outlined"
                onClick={onCancel}
                sx={{ marginLeft: 2 }}
              >
                Cancel
              </Button>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </>
  );
};

export default WorkExperienceForm;
